import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { Reveal, Breadcrumbs, CtaBand } from "@/components/Blocks";
import { BUSINESS } from "@/data/site";
import { SERVICES } from "@/data/services";

export default function CountyPage({ county: c }) {
    const crumbs = [
        { name: "Home", path: "/" },
        { name: "Service Areas", path: "/#areas" },
        { name: `${c.name}, FL`, path: `/${c.slug}` },
    ];
    return (
        <div>
            <Seo title={c.metaTitle} description={c.metaDesc} path={`/${c.slug}`} schemas={[breadcrumbSchema(crumbs)]} />

            <section className="bg-slate-950 text-white dark-grain">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14">
                    <Breadcrumbs items={crumbs} />
                    <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-3">Service area</p>
                    <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">{c.h1}</h1>
                    <p className="mt-4 text-slate-200 leading-relaxed max-w-3xl">{c.intro[0]}</p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14">
                {c.intro.slice(1).map((p, i) => (
                    <p key={i} className="text-slate-600 leading-relaxed mb-4 max-w-3xl">{p}</p>
                ))}

                <div className="grid sm:grid-cols-3 gap-4 mt-8">
                    {c.local.map((l, i) => (
                        <Reveal key={l.t} delay={i * 0.06}>
                            <div className="rounded-xl border border-slate-200 bg-white p-5 h-full shadow-sm">
                                <h2 className="font-heading font-bold text-slate-900 text-base">{l.t}</h2>
                                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{l.d}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-11 mb-5">
                    Communities we serve in {c.name}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="county-cities">
                    {c.cities.map((city) => (
                        city.slug ? (
                            <Link key={city.name} to={`/${city.slug}`}
                                className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:border-red-400 hover:shadow-md transition-all"
                                data-testid={`county-city-${city.slug}`}>
                                <MapPin className="h-4 w-4 text-red-600 shrink-0" />
                                <span className="font-medium text-slate-800 group-hover:text-red-600 text-sm">{city.name}</span>
                                <ArrowRight className="h-3.5 w-3.5 ml-auto text-slate-300 group-hover:text-red-500" />
                            </Link>
                        ) : (
                            <div key={city.name} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                                <MapPin className="h-4 w-4 text-slate-400 shrink-0" />
                                <span className="font-medium text-slate-600 text-sm">{city.name}</span>
                            </div>
                        )
                    ))}
                </div>

                <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-11 mb-5">
                    Appliance repair services in {c.name}
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {SERVICES.map((s) => (
                        <Link key={s.slug} to={`/${s.slug}`}
                            className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-red-400 hover:shadow-md transition-all"
                            data-testid={`county-service-${s.slug}`}>
                            <s.icon className="h-5 w-5 text-red-600 shrink-0" />
                            <span className="font-heading font-bold text-slate-900 text-sm group-hover:text-red-600">{s.name} in {c.name}</span>
                        </Link>
                    ))}
                </div>
            </section>

            <CtaBand
                title={<>Need appliance repair in {c.name}? <span className="text-amber-400">Call Appliance Mania.</span></>}
                sub={`In-home appliance repair across ${c.name} — $155 service call waived with completed repair, 6-month warranty on completed repairs. ${BUSINESS.phone}.`}
                testid={`county-cta-${c.slug}`}
            />
        </div>
    );
}
